import React, { cloneElement, useEffect, useState } from 'react';
import messages from './Messages';
import { SeriesRow, TitleWrapper } from './styles';
import ActionLink from '../ActionLink/ActionLink';
import Icon from '../Icon/Icon';
import { gray, red } from '../../utils/style/colors';
import HoverIcon from '../HoverIcon/HoverIcon';
import APopover from '../APopover';
import Input from '../Input/Input';
import Button from '../Button/Button';
import { ReportOverride } from './ChartViewer';
import { isANumber } from '../../utils/number';
import { isEqual } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { ITags, ReportViewType } from 'store/providers/providerTypes';
import {
  IMultipleAnswerQuestionView,
  IQuestionReportField,
  IQuestionsViewWithData,
  IReportView,
  ISeries,
  ISurveyView,
} from 'store/survey/surveyTypes';
import i18n from '../../i18n';

interface IChartTitleProps {
  tags: ITags | null;
  series?: ISeries;
  extraFilters?: HTMLElement[];
  report?: IReportView;
  saveReport?: (r: IReportView) => Promise<any>;
  override?: ReportOverride;
  survey?: ISurveyView;
  reportTitle?: string;
  onRemoveFromPrint?: () => void;
  simple?: boolean;
  display?: ReportViewType;
  numberOfSeries: number;
  index?: number;
  reports?: { [key: number]: IReportView };
}

const ChartTitle: React.FC<IChartTitleProps> = (props) => {
  const { series, survey, tags, display, numberOfSeries, report } = props;
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let name = report?.name || '';
    let expand = '';
    if (series?.type === 'question') {
      if (Number(series.questionId) === 0 || series?.questionId?.includes(','))
        expand = i18n.t('someQuestionsSplit', {
          ns: 'survey',
        }) as string;
      else
        expand =
          (i18n.t('in', { ns: 'survey' }) as string) +
          ' ' +
          (survey?.questionMap?.[Number(series.questionId)]?.data?.title ?? '');
    }
    if (series?.type === 'tag') {
      expand = i18n.t('someQuestionsSplit', {
        ns: 'survey',
      }) as string;
    }
    name = name.replace('(', expand + ' (');
    setName(name);
  }, [series]);
  if (
    !series ||
    display === 'Dashboard' ||
    (numberOfSeries <= 1 && (props.report?.id || 0) > 0)
  ) {
    return null;
  }
  let question: IQuestionsViewWithData | undefined;
  if (series.type === 'question' && series.questionId && survey) {
    question = survey?.questionMap?.[Number(series.questionId)];
  }

  function reportToView() {
    let newReport = {
      ...cloneDeep(props.report),
      series: [series],
    } as IReportView;

    newReport = {
      ...newReport,
      time: {
        ...newReport.time,
        timeLength: props.override?.timeLength || newReport.time.timeLength!,
        timeScale: props.override?.timeScale || newReport.time.timeScale!,
        similarPastYearPeriodLength: 0,
        offsetPeriod: 0,
      },
      series: [
        {
          ...newReport.series[0],
          chartType:
            props.override?.chartType || newReport.series![0].chartType!,
        },
      ],
    };
    Object.keys(props.override || {}).forEach((key) => {
      if (isANumber(Number(key)) && props.override![key]?.length > 0) {
        if (!newReport.fields) newReport.fields = [];
        newReport.fields.push({
          type: 'question',
          questionId: Number(key),
          choiceIds: props.override![key]?.map((it: any) => it.toString()),
        } as IQuestionReportField);
      }
    });
    newReport.name = name;
    delete (newReport as any).id;
    newReport.order = undefined;
    return newReport;
  }

  const newReport = reportToView();
  const similarReport = Object.values(props.reports || {}).find((it) => {
    if (!isEqual(it.fields, newReport.fields)) return false;
    if (!isEqual(it.time, newReport.time)) return false;
    if (it.series.length !== 1) {
      return false;
    }
    const seriesWithoutTypeA = Object.fromEntries(
      Object.entries(newReport.series[0]).filter(([_, v]) => !!v)
    );
    seriesWithoutTypeA.chartType = 'table';
    const seriesWithoutTypeB = Object.fromEntries(
      Object.entries(it.series[0]).filter(([_, v]) => !!v)
    );
    seriesWithoutTypeB.chartType = 'table';
    return isEqual(seriesWithoutTypeB, seriesWithoutTypeA);
  });
  return (
    <TitleWrapper showTopBorder={!!props.index}>
      <SeriesRow
        style={{ whiteSpace: props.onRemoveFromPrint ? 'initial' : 'nowrap' }}
      >
        <Icon type="bar-chart-2" color={gray} style={{ marginLeft: '10px' }} />
        {series.type === 'tag' && (
          <>
            <span> {messages.generateMetricName(series.metric) + ' '} </span>
            <span> {series?.split ? messages.comparing + '' : ''} </span>

            {series.tagId && (
              <span>{tags?.data?.[Number(series.tagId)]?.text}</span>
            )}
            {series.tagIds?.map((it) => {
              return (
                <span key={it}>
                  {(tags?.data?.[Number(it)]?.text || '') + ', '}
                </span>
              );
            })}
          </>
        )}
        {series.type === 'all' && (
          <span>{messages.generateMetricName(series.metric)}</span>
        )}
        {series.type === 'question' && survey && (
          <span>
            {series.subQuestionId ? (
              <React.Fragment>
                {question && question.data.type === 'grid' && (
                  <React.Fragment>
                    {question.data.parts?.find(
                      (part: any) => part.value === series.subQuestionId
                    )?.text || ''}
                  </React.Fragment>
                )}
                {question && question.data.type === 'indicator' && (
                  <React.Fragment>
                    {props.numberOfSeries > 1
                      ? question.data[series.subQuestionId]
                      : `${question.data[series.subQuestionId]} ${
                          i18n.t('from', { ns: 'survey' }) as string
                        } ${survey.questionMap?.[Number(series.questionId)]?.data.title}`}
                  </React.Fragment>
                )}
                {question && question.data.type === 'multipleChoice' && (
                  <React.Fragment>
                    {survey.questionMap?.[Number(series.questionId)]?.data
                      ?.title ||
                      '' + `(${messages.generateMetricName(series.metric)})`}
                  </React.Fragment>
                )}
                {question && question.data.type === 'multipleAnswer' && (
                  <React.Fragment>
                    {`${survey.questionMap?.[Number(series.questionId)]?.data.title} - ${(survey.questionMap?.[Number(series.questionId)]?.data as IMultipleAnswerQuestionView)?.questionFields?.find((f) => f.key === series.subQuestionId)?.title || messages.generateMetricName(series.metric)}`}
                  </React.Fragment>
                )}
              </React.Fragment>
            ) : series.metric === 'responseList' ? (
              <React.Fragment>
                {
                  i18n.t('recordedTextResponseList', {
                    ns: 'survey',
                  }) as string
                }
              </React.Fragment>
            ) : Number(series.questionId) === 0 ? (
              <React.Fragment>
                {(i18n.t('allQuestionsSplit', {
                  ns: 'survey',
                }) as string) +
                  `(${messages.generateMetricName(series.metric)})`}
              </React.Fragment>
            ) : series.questionId?.includes(',') ? (
              <React.Fragment>
                {(i18n.t('someQuestionsSplit', {
                  ns: 'survey',
                }) as string) +
                  `(${messages.generateMetricName(series.metric)})`}
              </React.Fragment>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  textWrap: 'wrap',
                }}
              >
                {/* ${survey.questionMap?.[Number(series.questionId)]?.data.title}/ */}
                {`${(survey.questionMap?.[Number(series.questionId)]?.data as IMultipleAnswerQuestionView)?.questionFields?.find((f) => f.key === series.splitOnFieldValues)?.title || messages.generateMetricName(series.metric)} ${series.questionId ? (series.questionId?.split(',').length > 1 ? i18n.t('splitOnQuestions', { ns: 'survey' }) : i18n.t('splitOnAQuestion', { ns: 'survey', question: (survey.questionMap?.[Number(series.questionId)]?.data as IMultipleAnswerQuestionView)?.title }) || '') : ''}`}
              </div>
            )}
          </span>
        )}
        {props.extraFilters?.map((it: any) =>
          cloneElement(it, {
            className: 'showOnScreenshot',
            style: { display: 'none' },
          })
        )}
        {props.saveReport && (props.report?.id || 0) < 0 && (
          <APopover
            arrow
            trigger="click"
            curveBorder
            target={
              <span className="">
                <HoverIcon
                  type={similarReport ? 'edit-2' : 'save'}
                  color={'blue'}
                  size={'14px'}
                />{' '}
              </span>
            }
          >
            <div>
              <Input
                label={i18n.t('reportName', { ns: 'survey' }) as string}
                placeholder={i18n.t('reportName', { ns: 'survey' }) as string}
                id={props.report?.id?.toString() || 'save'}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: '500px' }}
              />
              {!!similarReport && similarReport.name !== name && (
                <p>
                  {i18n.t('currentName', { ns: 'survey' }) as string}:
                  {similarReport.name}{' '}
                </p>
              )}
              <Button
                loading={loading}
                disabled={!name}
                onClick={() => {
                  setLoading(true);
                  const finalToSaveReport = similarReport
                    ? cloneDeep(similarReport)
                    : newReport;
                  finalToSaveReport.name = name;
                  props.saveReport!(finalToSaveReport).then(() => {
                    setLoading(false);
                  });
                }}
              >
                {similarReport
                  ? (i18n.t('update', { ns: 'survey' }) as string)
                  : (i18n.t('save', { ns: 'survey' }) as string)}
              </Button>
            </div>
          </APopover>
        )}
      </SeriesRow>

      {props.onRemoveFromPrint && (
        <ActionLink className="hide-on-print" onClick={props.onRemoveFromPrint}>
          <Icon type="trash" color={red} />
        </ActionLink>
      )}
    </TitleWrapper>
  );
};

export default ChartTitle;
